import { Controller } from "@hotwired/stimulus";
import {
  MIN_REMOTE_QUERY_LENGTH,
  NavigationRecordCache,
  REMOTE_SEARCH_DEBOUNCE_MS,
  isCommandSearchShortcut,
  normalizeTransientNavigationState,
  platformModifierLabel,
  rankNavigationResults,
} from "../lib/partner_navigation_search.mjs";

const GROUP_LABELS = { page: "Pages", view: "Saved views", claim: "Claims", member: "Members" };
// A record can arrive with nothing to show under its title (a claim with no member,
// facility, or service date yet), so the secondary line falls back per type.
const FALLBACK_META = { claim: "Open claim", member: "Open member", view: "Saved view" };

export default class extends Controller {
  static targets = ["overlay", "input", "results", "status", "modifier"];
  static values = {
    pages: Array,
    views: Array,
    recordIcons: Object,
    searchUrl: String,
  };

  connect() {
    this.records = [];
    this.recordsQuery = "";
    this.visibleResults = [];
    this.selectedIndex = -1;
    this.searchOpen = false;
    this.loading = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previouslyFocusedElement = null;
    this.cache = new NavigationRecordCache();

    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    const modifier = platformModifierLabel(platform);
    this.modifierTargets.forEach((target) => { target.textContent = modifier; });
  }

  disconnect() {
    this.prepareForCache();
  }

  handleGlobalKeydown(event) {
    if (isCommandSearchShortcut(event)) {
      event.preventDefault();
      this.searchOpen ? this.closeSearch() : this.openSearch();
      return;
    }

    if (event.key !== "Escape" || !this.searchOpen) return;

    event.preventDefault();
    this.closeSearch();
  }

  openSearch() {
    this.previouslyFocusedElement = document.activeElement;
    this.searchOpen = true;
    this.overlayTarget.hidden = false;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.inputTarget.value = "";
    this.records = [];
    this.recordsQuery = "";
    this.loading = false;
    this.renderResults();
    window.requestAnimationFrame(() => this.inputTarget.focus());
  }

  closeSearch() {
    this.searchOpen = false;
    this.overlayTarget.hidden = true;
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.restoreBodyScroll();
    if (this.previouslyFocusedElement?.isConnected) this.previouslyFocusedElement.focus();
    this.previouslyFocusedElement = null;
  }

  prepareForCache() {
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    normalizeTransientNavigationState({
      overlay: this.hasOverlayTarget ? this.overlayTarget : null,
      body: document.body,
      previousBodyOverflow: this.previousBodyOverflow,
    });
    this.searchOpen = false;
    this.loading = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previousBodyOverflow = undefined;
    this.previouslyFocusedElement = null;
  }

  closeFromBackdrop(event) {
    if (event.target === this.overlayTarget) this.closeSearch();
  }

  search() {
    window.clearTimeout(this.searchTimer);
    const query = this.query();

    if (query.length < MIN_REMOTE_QUERY_LENGTH) {
      this.abortController?.abort();
      this.showRecords([], query);
      return;
    }

    const cached = this.cache.get(query);
    if (cached || this.cache.hasEmptyPrefix(query)) {
      this.abortController?.abort();
      this.showRecords(cached || [], query);
      return;
    }

    // Keep the records already on screen while the request is in flight, but only the
    // ones that still match what is typed (see recordsAreCurrent in renderResults), so
    // the list does not blank out mid-typing and cannot leave an unrelated record
    // selected either.
    this.loading = true;
    this.renderResults();
    this.searchTimer = window.setTimeout(() => this.fetchRemoteResults(query), REMOTE_SEARCH_DEBOUNCE_MS);
  }

  handleInputKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;

    event.preventDefault();
    if (!this.visibleResults.length) return;

    if (event.key === "ArrowDown") {
      this.selectIndex((this.selectedIndex + 1) % this.visibleResults.length);
    } else if (event.key === "ArrowUp") {
      const previous = this.selectedIndex <= 0 ? this.visibleResults.length - 1 : this.selectedIndex - 1;
      this.selectIndex(previous);
    } else {
      this.activateResult(this.visibleResults[Math.max(this.selectedIndex, 0)], event.metaKey || event.ctrlKey);
    }
  }

  selectFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    this.selectIndex(Number(result.dataset.resultIndex), false);
  }

  activateFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    event.preventDefault();
    this.activateResult(this.visibleResults[Number(result.dataset.resultIndex)], event.metaKey || event.ctrlKey);
  }

  async fetchRemoteResults(query) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Navigation search failed with ${response.status}`);

      const payload = await response.json();
      const records = [...(payload.claims || []), ...(payload.members || [])];
      this.cache.set(query, records);
      if (query !== this.query()) return;

      this.showRecords(records, query);
    } catch (error) {
      if (error.name === "AbortError") return;
      // An older request can fail while a newer query is mid-debounce; its failure says
      // nothing about what is typed now.
      if (query !== this.query()) return;

      // Drop the records so the error status is not sitting above results that no longer
      // correspond to what was typed, but keep rendering the page and saved-view
      // shortcuts the status says still work.
      this.showRecords([], query);
      this.renderStatus(`
        <div class="text-sm font-semibold text-support-dark-800">Record search is unavailable</div>
        <div class="mt-1 text-xs text-support-dark-450">Page shortcuts still work.</div>
      `);
    }
  }

  showRecords(records, query) {
    this.records = records;
    this.recordsQuery = query;
    this.loading = false;
    this.renderResults();
  }

  renderResults() {
    const query = this.query().toLowerCase();
    this.visibleResults = rankNavigationResults(
      [...this.pagesValue, ...this.viewsValue],
      this.records,
      query,
      { recordsAreCurrent: this.recordsQuery === this.query() }
    );

    let lastType = null;
    this.resultsTarget.innerHTML = this.visibleResults.map((result, index) => {
      const header = result.type === lastType ? "" : this.groupHeader(result.type);
      lastType = result.type;
      return header + this.resultRow(result, index, query);
    }).join("");

    if (this.visibleResults.length) {
      this.statusTarget.classList.add("hidden");
      this.selectIndex(0, false);
    } else {
      this.selectedIndex = -1;
      this.inputTarget.removeAttribute("aria-activedescendant");
      this.renderStatus(this.loading ? this.loadingState() : this.emptyState(this.query()));
    }
  }

  groupHeader(type) {
    // role="presentation" keeps the listbox's children valid: only the rows are options.
    return `<div role="presentation" class="px-3 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-support-dark-500 first:pt-2">${GROUP_LABELS[type] || "Results"}</div>`;
  }

  // iconHtml is server-rendered icon markup; everything the user or the database
  // supplied goes through highlight()/escapeHtml().
  resultRow(result, index, query) {
    const iconHtml = result.icon_html || this.recordIconsValue[result.type];

    return `
      <a href="${this.escapeHtml(result.url)}" role="option" id="partner-navigation-result-${index}" data-result-index="${index}" aria-selected="false" tabindex="-1"
         class="flex items-center gap-3.5 rounded-md px-3 py-2.5 transition-colors hover:bg-support-light-250">
        <span data-icon-chip class="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-support-light-250 text-support-dark-600">${iconHtml || ""}</span>
        <span class="min-w-0">
          <span class="block truncate text-sm font-semibold text-support-dark-800">${this.highlight(result.title, query)}</span>
          <span class="block truncate text-xs text-support-dark-450">${this.highlight(result.meta || FALLBACK_META[result.type] || "Open page", query)}</span>
        </span>
        <span class="ml-auto flex flex-none items-center gap-2.5">
          ${result.amount ? `<span class="text-[13px] font-bold tabular-nums text-support-dark-600">${this.escapeHtml(result.amount)}</span>` : ""}
          <span data-enter-hint class="hidden h-5 min-w-5 items-center justify-center rounded border border-support-light-300 bg-white px-1 text-[11px] leading-none text-support-dark-600">↵</span>
        </span>
      </a>
    `;
  }

  loadingState() {
    return `<div class="text-sm text-support-dark-450">Searching claims and members…</div>`;
  }

  emptyState(query) {
    const guidance = query.length >= MIN_REMOTE_QUERY_LENGTH
      ? `<div class="text-sm font-semibold text-support-dark-800">No matches for \u201C${this.escapeHtml(query)}\u201D</div>
         <div class="mt-1 text-xs text-support-dark-450">Search by page, saved view, claim ID, member name, facility, or employer.</div>`
      : `<div class="text-sm font-semibold text-support-dark-800">Keep typing to search records</div>
         <div class="mt-1 text-xs text-support-dark-450">2+ characters finds claims and members.</div>`;

    return `<span class="mb-2.5 flex justify-center text-support-dark-500">${this.recordIconsValue.empty || ""}</span>${guidance}`;
  }

  renderStatus(html) {
    this.statusTarget.innerHTML = html;
    this.statusTarget.classList.remove("hidden");
  }

  selectIndex(index, shouldScroll = true) {
    this.selectedIndex = index;
    this.resultsTarget.querySelectorAll("[data-result-index]").forEach((element, elementIndex) => {
      const selected = elementIndex === index;
      element.setAttribute("aria-selected", String(selected));
      element.classList.toggle("bg-complementary-purple-200", selected);

      const chip = element.querySelector("[data-icon-chip]");
      chip?.classList.toggle("bg-support-light-250", !selected);
      chip?.classList.toggle("text-support-dark-600", !selected);
      chip?.classList.toggle("bg-white", selected);
      chip?.classList.toggle("text-core-purple-700", selected);

      const enterHint = element.querySelector("[data-enter-hint]");
      enterHint?.classList.toggle("hidden", !selected);
      enterHint?.classList.toggle("flex", selected);

      if (selected && shouldScroll) element.scrollIntoView({ block: "nearest" });
    });

    if (index >= 0) this.inputTarget.setAttribute("aria-activedescendant", `partner-navigation-result-${index}`);
  }

  activateResult(result, newTab) {
    if (!result?.url) return;

    if (newTab) {
      window.open(result.url, "_blank", "noopener,noreferrer");
      return;
    }

    this.prepareForCache();
    if (window.Turbo?.visit) {
      window.Turbo.visit(result.url);
    } else {
      window.location.assign(result.url);
    }
  }

  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (character) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character]
    ));
  }

  // Escapes everything, then wraps the typed substring in <b> so the results container's
  // [&_b]: styling can tint the match purple.
  highlight(value, query) {
    const text = String(value ?? "");
    if (!query) return this.escapeHtml(text);

    const haystack = text.toLowerCase();
    let output = "";
    let index = 0;

    while (true) {
      const match = haystack.indexOf(query, index);
      if (match === -1) {
        output += this.escapeHtml(text.slice(index));
        break;
      }

      output += this.escapeHtml(text.slice(index, match)) +
        `<b>${this.escapeHtml(text.slice(match, match + query.length))}</b>`;
      index = match + query.length;
    }

    return output;
  }

  query() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : "";
  }

  restoreBodyScroll() {
    if (this.previousBodyOverflow === undefined) return;

    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = undefined;
  }
}
